import { Link } from "react-router-dom";
import Seo from "@/components/Seo";
import { Reveal, GoldLine, MaskedLine } from "@/components/Reveal";
import { openBooking } from "@/lib/api";
import { BRAND_FULL, ADDRESS } from "@/config/site";

const CHAPTERS = [
    {
        n: "01", title: "Our Story",
        copy: "In the heart of Rani Ka Bagh, Amritsar, BSK Jewellers — Bhimsen Khanna Chandiwale — was built on a simple belief: jewellery is not sold, it is entrusted. Families return to us because a piece bought here carries meaning beyond its weight in gold.",
        image: "/images/gold-set.jpg",
    },
    {
        n: "02", title: "Our Philosophy",
        copy: "We believe luxury is quiet. It lives in the weight of a bangle, the exactness of a setting, the honesty of a conversation. We would rather show you one perfect piece than a hundred ordinary ones.",
        image: "/images/earrings-pearl.jpg",
    },
    {
        n: "03", title: "Our Craftsmanship",
        copy: "Every piece in our showroom is selected or created with obsessive attention — the fall of a necklace, the balance of an earring, the finish on the inside of a ring that only the wearer will ever feel.",
        image: "/images/goldsmith-1.jpg",
    },
    {
        n: "04", title: "Our Collections",
        copy: "Gold in the great Punjabi tradition. Diamonds chosen stone by stone. Bridal jewellery composed like couture. And bespoke pieces that begin with your story and end as an heirloom.",
        image: "/images/ring-macro.jpg",
    },
    {
        n: "05", title: "Our Customer Experience",
        copy: "No pressure, no haste. A private conversation, honest guidance on purity and price, and jewellery shown the way it deserves to be seen. That is the BSK way — and it is why our customers become our family.",
        image: "/images/jewellery-model.jpg",
    },
];

const About = () => (
    <main>
        <Seo
            title="Our Story — Bhimsen Khanna Chandiwale | BSK Jewellers Amritsar"
            siteName="BSK Jewellers"
            description={`The story behind BSK Jewellers (${BRAND_FULL}), a premium jewellery destination in ${ADDRESS} — our philosophy, craftsmanship and customer experience.`}
            image="/images/flatlay.jpg"
        />
        <section className="pt-40 pb-16 sm:pt-48 text-center px-5" data-testid="about-hero">
            <span className="eyebrow">{BRAND_FULL}</span>
            <h1 className="font-serif text-4xl sm:text-6xl lg:text-7xl leading-[1.05] mt-6 max-w-4xl mx-auto">
                <MaskedLine delay={0.15}>The Story Behind</MaskedLine>
                <MaskedLine delay={0.3}><span className="italic gold-text">BSK Jewellers</span></MaskedLine>
            </h1>
            <Reveal delay={0.5}>
                <p className="text-sand text-sm sm:text-base max-w-xl mx-auto mt-7">Rani Ka Bagh · Amritsar · Punjab</p>
            </Reveal>
        </section>

        <section className="pb-24 sm:pb-32">
            <div className="max-w-[1300px] mx-auto px-5 sm:px-8 space-y-24 sm:space-y-32">
                {CHAPTERS.map((c, i) => (
                    <div key={c.n} className={`grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 items-center`} data-testid={`about-chapter-${c.n}`}>
                        <Reveal className={`md:col-span-7 ${i % 2 ? "md:order-2" : ""}`}>
                            <div className="relative">
                                <span className="absolute -top-10 -left-3 font-serif italic text-[6rem] sm:text-[8rem] leading-none text-gold/10 select-none" aria-hidden="true">{c.n}</span>
                                <div className="aspect-[16/10] overflow-hidden border border-gold/15 group">
                                    <img src={c.image} alt={`${c.title} — BSK Jewellers Amritsar`} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-1000" />
                                </div>
                            </div>
                        </Reveal>
                        <div className={`md:col-span-5 ${i % 2 ? "md:order-1 md:text-right" : ""}`}>
                            <Reveal delay={0.15}>
                                <span className="eyebrow">Chapter {c.n}</span>
                                <h2 className="font-serif text-3xl sm:text-4xl mt-4">{c.title}</h2>
                                <GoldLine delay={0.25} className={`w-16 my-6 ${i % 2 ? "md:ml-auto" : ""}`} />
                                <p className="text-sand text-sm sm:text-base leading-relaxed">{c.copy}</p>
                            </Reveal>
                        </div>
                    </div>
                ))}
            </div>
        </section>

        <section className="py-24 bg-espresso/60 border-t border-gold/10 text-center">
            <div className="max-w-2xl mx-auto px-5">
                <Reveal>
                    <h2 className="font-serif text-3xl sm:text-4xl">Visit Our <span className="italic gold-text">Showroom</span></h2>
                    <p className="text-sand text-sm mt-5">The story continues in person — we would be honoured to welcome you.</p>
                    <div className="flex flex-col sm:flex-row justify-center gap-4 mt-9">
                        <Link to="/visit" data-testid="about-visit-btn" className="bg-gold text-obsidian text-[11px] tracking-[0.25em] uppercase px-9 py-4 hover:bg-gold-bright transition-colors">Plan Your Visit</Link>
                        <button onClick={() => openBooking()} data-testid="about-book-btn" className="border border-gold/50 text-gold text-[11px] tracking-[0.25em] uppercase px-9 py-4 hover:bg-gold hover:text-obsidian transition-colors">Book an Appointment</button>
                    </div>
                </Reveal>
            </div>
        </section>
    </main>
);

export default About;
